import React from 'react';
import { render, screen } from '@testing-library/react';
import SocialButton from './SocialButton';

jest.mock('components/styleguide/icon/Icon', () => ({
  __esModule: true,
  default: ({ name, ...props }: any) => <span data-icon={name} {...props} />,
}));

jest.mock('components/styleguide/button/Button', () => ({
  __esModule: true,
  default: ({ children, iconLeft, disabled, 'aria-label': ariaLabel, className }: any) => (
    <button disabled={disabled} aria-label={ariaLabel} data-icon={iconLeft} className={className}>
      {children}
    </button>
  ),
}));

describe('SocialButton', () => {
  it('renders provider name', () => {
    render(<SocialButton provider="Google" href="/auth/google" />);
    expect(screen.getByText('Google')).toBeInTheDocument();
  });

  it('renders as a link when not disabled', () => {
    render(<SocialButton provider="Google" href="/auth/google" />);
    const link = screen.getByRole('link', { name: 'Sign in with Google' });
    expect(link).toHaveAttribute('href', '/auth/google');
  });

  it('sets data-method on the link', () => {
    render(<SocialButton provider="Google" href="/auth/google" />);
    expect(screen.getByRole('link')).toHaveAttribute('data-method', 'post');
  });

  it('supports custom data-method', () => {
    render(<SocialButton provider="Google" href="/auth/google" dataMethod="get" />);
    expect(screen.getByRole('link')).toHaveAttribute('data-method', 'get');
  });

  it('renders provider icon', () => {
    const { container } = render(<SocialButton provider="Google" href="/auth/google" />);
    expect(container.querySelector('[data-icon="google"]')).toBeInTheDocument();
  });

  it('marks icon as aria-hidden', () => {
    const { container } = render(<SocialButton provider="Google" href="/auth/google" />);
    expect(container.querySelector('[data-icon="google"]')).toHaveAttribute('aria-hidden', 'true');
  });

  describe('accessibility', () => {
    it('has aria-label on link', () => {
      render(<SocialButton provider="Google" href="/auth/google" />);
      expect(screen.getByLabelText('Sign in with Google')).toBeInTheDocument();
    });

    it('has aria-label on disabled button', () => {
      render(<SocialButton provider="Google" href="/auth/google" disabled />);
      expect(screen.getByLabelText('Sign in with Google')).toBeInTheDocument();
    });

    it('sets noopener noreferrer for blank target', () => {
      render(<SocialButton provider="Google" href="/auth/google" target="_blank" />);
      expect(screen.getByRole('link')).toHaveAttribute('rel', 'noopener noreferrer');
    });

    it('uses custom rel when provided', () => {
      render(<SocialButton provider="Google" href="/auth/google" target="_blank" rel="nofollow" />);
      expect(screen.getByRole('link')).toHaveAttribute('rel', 'nofollow');
    });
  });

  describe('disabled state', () => {
    it('renders as a button when disabled', () => {
      render(<SocialButton provider="Google" href="/auth/google" disabled />);
      expect(screen.getByRole('button')).toBeDisabled();
    });

    it('does not render a link when disabled', () => {
      render(<SocialButton provider="Google" href="/auth/google" disabled />);
      expect(screen.queryByRole('link')).not.toBeInTheDocument();
    });
  });

  describe('providers', () => {
    const providers = ['Google', 'Microsoft', 'Linkedin', 'Facebook', 'Github'] as const;

    providers.forEach(provider => {
      it(`renders ${provider} button`, () => {
        const { container } = render(<SocialButton provider={provider} href={`/auth/${provider.toLowerCase()}`} />);
        expect(screen.getByText(provider)).toBeInTheDocument();
        expect(container.querySelector(`[data-icon="${provider.toLowerCase()}"]`)).toBeInTheDocument();
      });
    });
  });

  it('applies custom className', () => {
    render(<SocialButton provider="Google" href="/auth/google" className="custom" />);
    expect(screen.getByRole('link')).toHaveClass('custom');
  });
});
